<template>
  <div v-for="item in summary" :key="item?.title">
    <div v-if="item?.title" class="mpf-summary-list__header">
      <h2 class="mpf-heading-m">{{ item.title }}</h2>
      <div v-if="item.changeUrl">
        <RouterLink class="mpf-link" :to="item.changeUrl">
          Change <span class="mpf-visually-hidden"> {{ item.title }} content</span></RouterLink
        >
      </div>
    </div>
    <dl v-if="item?.title" class="mpf-summary-list">
      <template v-for="field in item.fields" :key="field.label ?? field.label">
        <!-- When field.value is an array of subfields -->
        <template v-if="Array.isArray(field.value) && field.value.length">
          <div
            v-for="subField in field.value"
            :key="subField.label ?? subField.label"
            class="mpf-summary-list__row"
          >
            <dt class="mpf-summary-list__key is_array">{{ subField.label }}</dt>
            <dd class="mpf-summary-list__value is_array" data-private>{{ subField.value }}</dd>
          </div>
        </template>

        <!-- When field is a simple value -->

        <div v-else-if="field.label && field.value" class="mpf-summary-list__row">
          <dt class="mpf-summary-list__key">{{ field.label }}</dt>
          <dd class="mpf-summary-list__value" data-private>{{ field.value }}</dd>
        </div>
      </template>
    </dl>
  </div>

  <div v-if="store?.attachments?.length" class="mpf-u-margin-bottom-6">
    <h2 class="mpf-heading-m">Attachments</h2>
    <mpf-files-summary
      v-model="store.attachments"
      class="mpf-u-margin-bottom-4"
      :allow-delete="true"
    />
  </div>

  <div v-if="summary?.length">
    <mpf-button
      label="Submit request"
      @click="handleSubmit"
      :disabled="apiLoading"
      prevent-double-click
    />
  </div>
</template>

<script setup lang="ts">
  import { useMedicalRequestStore } from '@/stores/useMedicalRequestStore';
  import { useRequestStoresCommon } from '../composables/useRequestStoresCommon';
  import {
    inputAdminRequest,
    inputDateOfBirth,
    inputFirstName,
    inputProblemDescription,
    inputLastName,
    inputPostcode,
    inputProblemRecentContact,
    inputReporterType,
    inputRelationshipToPatient,
  } from '@/data/inputs';
  import MpfButton from '../components/MpfButton/MpfButton.vue';
  import { storeToRefs } from 'pinia';
  import { router } from '@/router';
  import { useAdminRequestStore } from '@/stores/useAdminRequestStore';
  import { useUrlSegments } from '../composables/useUrlSegments';
  import { useRepeatPrescriptionRequestStore } from '@/stores/useRepeatPrescriptionRequestStore';
  import { computed } from 'vue';
  import MpfFilesSummary from '../components/MpfFilesSummary/MpfFilesSummary.vue';
  import { formatLongDate } from '../helpers/dates';
  import { RequestType } from '@/types';
  import { useRequestFormAvailability } from '../composables/useRequestFormAvailability';
  import { useNhsAppStore } from '@/stores/useNhsAppStore';

  const medicalRequestStore = useMedicalRequestStore();
  const adminRequestStore = useAdminRequestStore();
  const repeatPrescriptionRequestStore = useRepeatPrescriptionRequestStore();
  const nhsAppStore = useNhsAppStore();

  const { isNhsWebApp, isOpenInNhsApp } = storeToRefs(nhsAppStore);

  const storesCommon = useRequestStoresCommon();
  const { check } = useRequestFormAvailability();
  const { firstSegment } = useUrlSegments();
  const store = storesCommon.tryGetStore(firstSegment.value as RequestType);
  const { apiLoading } = storeToRefs(store);

  const handleSubmit = async () => {
    await check(firstSegment.value as RequestType);
    if (apiLoading.value) return;

    const submitActioResult = await store.submitAction();
    if (submitActioResult.ok) {
      await router.push(`/${firstSegment.value}/submitted-successfully`);
    } else {
      console.error('submitActioResult', submitActioResult, firstSegment.value);
    }
  };

  const mapPrescriptionsToLabel = (ids: string[]) => {
    const source = repeatPrescriptionRequestStore.prescriptionsList;
    return source
      .filter((p) => ids.includes(p.id)) // keep only matching IDs
      .map((p) => ({
        label: p.productName,
        value: `${p.quantity}`,
      }));
  };

  const mapPrescriptionsAcuteToLabel = (ids: string[]) => {
    const source = repeatPrescriptionRequestStore.prescriptionsAcuteList;
    return source
      .filter((p) => ids.includes(p.id)) // keep only matching IDs
      .map((p) => ({
        label: p.productName,
        value: `${p.quantity}`,
      }));
  };

  const reporterTypeLabels = { PATIENT: 'Me', PROXY: 'Someone else' };
  //
  const proxySummaryDetails = computed(() => {
    return store.isProxy
      ? {
          title: 'Your details',
          fields: [
            { label: inputFirstName.label, value: store.proxyDetails?.firstName },
            { label: inputLastName.label, value: store.proxyDetails?.lastName },
            {
              label: inputRelationshipToPatient.label,
              value: store.proxyDetails?.relationshipToPatient,
            },
          ],
          changeUrl:
            isNhsWebApp.value || isOpenInNhsApp.value ? null : `/${store.$id}/your-details`,
        }
      : null;
  });

  const patientSummaryDetails = computed(() => {
    return {
      title: store.isProxy ? 'Patient details' : 'Your details',
      fields: [
        { label: inputFirstName.label, value: store.patientDetails?.firstName },
        { label: inputLastName.label, value: store.patientDetails?.lastName },
        {
          label: inputDateOfBirth.label,
          value: formatLongDate(store.patientDetails?.dateOfBirth),
        },
        { label: inputPostcode.label, value: store.patientDetails?.postcode },
      ],
      changeUrl: isNhsWebApp.value || isOpenInNhsApp.value ? null : `/${store.$id}/patient-details`,
    };
  });

  const summary = computed(() => {
    if (firstSegment.value === 'medical-request') {
      return [
        isNhsWebApp.value || isOpenInNhsApp.value
          ? null
          : {
              title: 'Requester details',
              fields: [
                {
                  label: inputReporterType.label,
                  value: reporterTypeLabels[medicalRequestStore.reporterType],
                },
              ],
              changeUrl: '/medical-request/request-is-for',
            },
        proxySummaryDetails.value,
        patientSummaryDetails.value,
        {
          title: 'Problem details',
          fields: [
            { label: inputProblemDescription.label, value: medicalRequestStore.problemDescription },
            {
              label: inputProblemRecentContact.label,
              value: medicalRequestStore.problemRecentContact,
            },
          ],
          changeUrl: '/medical-request/problem-details',
        },
      ];
    } else if (firstSegment.value === 'repeat-prescription-request') {
      return [
        {
          title: 'Medicines to be prescribed',
          fields: [
            ...(repeatPrescriptionRequestStore.selectedPrescriptionsList?.length
              ? [
                  {
                    label: 'Selected prescriptions',
                    value: mapPrescriptionsToLabel(
                      repeatPrescriptionRequestStore.selectedPrescriptionsList,
                    ),
                  },
                ]
              : []),
            ...(repeatPrescriptionRequestStore.selectedPrescriptionsAcuteList?.length
              ? [
                  {
                    label: 'Selected acute prescriptions',
                    value: mapPrescriptionsAcuteToLabel(
                      repeatPrescriptionRequestStore.selectedPrescriptionsAcuteList,
                    ),
                  },
                ]
              : []),
          ],
          changeUrl: '/repeat-prescription-request/prescriptions-list',
        },
        // Only shown when the patient ticked the box and entered a note.
        ...(repeatPrescriptionRequestStore.additionalInformation?.trim()
          ? [
              {
                title: 'Your note',
                fields: [
                  {
                    label: 'Note about this request',
                    value: repeatPrescriptionRequestStore.additionalInformation,
                  },
                ],
                changeUrl: '/repeat-prescription-request/prescriptions-list',
              },
            ]
          : []),
      ];
    } else if (firstSegment.value === 'admin-request') {
      return [
        isNhsWebApp.value || isOpenInNhsApp.value
          ? null
          : {
              title: 'Requester details',
              fields: [
                {
                  label: inputReporterType.label,
                  value: reporterTypeLabels[adminRequestStore.reporterType],
                },
              ],
              changeUrl:
                isNhsWebApp.value || isOpenInNhsApp.value ? null : '/admin-request/request-is-for',
            },
        proxySummaryDetails.value,
        patientSummaryDetails.value,
        {
          title: 'Request details',
          fields: [{ label: inputAdminRequest.label, value: adminRequestStore.adminRequest }],
          changeUrl: '/admin-request/admin-problem-details',
        },
      ];
    }
    return null; // optional fallback
  });
</script>
<style scoped>
  .mpf-summary-list__header {
    display: flex;
    justify-content: space-between;
  }
</style>
